<template>
  <div class="announcement-edit">
    <div class="header">
      <h2>{{ isEdit ? '编辑公告' : '新增公告' }}</h2>
      <a-button type="primary" @click="handleBack">
        <i class="iconfont icon-fanhuiicon mr5"></i> 返回
      </a-button>
    </div>

    <div class="form-container">
      <a-form
          ref="formRef"
          :model="formData"
          :rules="formRules"
          layout="vertical"
          class="edit-form"
      >
        <a-form-item label="公告标题" name="title">
          <a-input
              v-model:value="formData.title"
              placeholder="请输入公告标题"
              :maxlength="100"
              show-count
              class="large-input"
          />
        </a-form-item>

        <a-form-item label="公告内容" name="content">
          <a-textarea
              v-model:value="formData.content"
              placeholder="请输入公告内容"
              :auto-size="{ minRows: 7, maxRows: 25 }"
              class="large-textarea"
          />
        </a-form-item>

        <a-form-item label="发布状态" name="status">
          <a-radio-group v-model:value="formData.status">
            <a-radio value="0">保存为草稿</a-radio>
            <a-radio value="1">立即发布</a-radio>
          </a-radio-group>
        </a-form-item>

        <a-form-item label="附件上传" name="fileList">
          <fileUpload
              class="pl64"
              :updateTitle="'点击上传'"
              ref="uploadRef"
              accept=".doc,.docx,.pdf,.jpg,.png,.xls,.xlsx"
              :maxCount="5"
              pathName="announcement"
              @fileListChange="onFileListChange">
          </fileUpload>
          <p class="upload-tip">支持PDF、Word、Excel格式文件，单个文件大小不超过10MB</p>
        </a-form-item>

        <a-form-item class="form-actions">
          <a-button @click="handleBack">取消</a-button>
          <a-button class="ml10" type="primary" @click="handleSubmit">
            {{ isEdit ? '保存修改' : '确认新增' }}
          </a-button>
        </a-form-item>
      </a-form>
    </div>
  </div>
</template>

<script setup>
import {computed, onMounted, ref} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import {message, Modal} from 'ant-design-vue';
import useUserStore from '@/store/modules/user';
import {add, getById, update} from '@/api/announcement/index.js';
import fileUpload from '@/components/fileUpload';

const router = useRouter();
const route = useRoute();
const store = useUserStore();
const formRef = ref();
const fileList = ref([])
const uploadRef = ref();

// 判断是否为编辑模式
const isEdit = computed(() => !!route.query.id);

// 表单数据
const formData = ref({
  id: null,
  title: '',
  content: '',
  status: '0', // 默认为草稿
  attachments: []
});

// 表单验证规则
const formRules = {
  title: [
    { required: true, message: '请输入公告标题', trigger: 'blur' },
    { min: 1, max: 100, message: '标题长度不能超过100个字符', trigger: 'blur' }
  ],
  content: [
    { required: true, message: '请输入公告内容', trigger: 'blur' }
  ]
};

// 处理文件上传变化
const onFileListChange = (newFileList) => {
  // 只存储状态为done的文件的URL地址
  fileList.value = newFileList
      .filter(file => file.status === 'done')
      .map(file => {
        // 根据不同情况获取URL
        if (file.url) return file.url;
        return '';
      });
};

// 获取编辑数据
const getEditData = async () => {
  if (isEdit.value) {
    try {
      const res = await getById(route.query.id);
      if (res.code === 200) {
        const data = res.data;
        formData.value = data;
        if (data.attachments && Array.isArray(data.attachments)) {
          // 修改附件列表处理逻辑，从URL地址数组转换为包含文件名的对象数组
          const fileObjects = data.attachments.map((url, index) => {
            // 从URL中提取文件名，如果无法提取则使用默认名称
            const urlParts = url.split('/');
            let fileName = urlParts[urlParts.length - 1] || `附件${index + 1}`;

            // 解码URL编码的文件名
            fileName = decodeURIComponent(fileName);

            return {
              uid: `file_${index}`,
              name: fileName,
              url: url
            };
          });
          if (uploadRef.value) {
            uploadRef.value.setFileList(fileObjects);
          }
        }
      }
    } catch (error) {
      message.error('获取公告信息失败');
      console.error('获取公告信息失败:', error);
    }
  }
};

// 提交表单
const handleSubmit = async () => {
  try {
    // 表单验证
    await formRef.value.validate();

    console.log("fileList", fileList);
    // 处理上传的文件
    formData.value.attachments = fileList.value;

    console.log("formData", formData.value)
    if (isEdit.value) {
      const res = await update(formData.value)
      if (res.code === 200) {
        message.success('公告更新成功');
        await router.push('/announcement/index');
      } else {
        message.error(res.message || '操作失败');
      }
    }else {
      const res = await add(formData.value)
      if (res.code === 200) {
        message.success("公告新增成功");
        await router.push('/announcement/index');
      } else {
        message.error(res.message || '操作失败');
      }
    }
  } catch (error) {
    message.error('操作失败，请重试');
    console.error('提交表单出错:', error);
  }
};

// 取消操作
const handleCancel = () => {
  if (formRef.value && formRef.value.isFieldsTouched()) {
    Modal.confirm({
      title: '提示',
      content: '当前表单已修改，确定要离开吗？',
      okText: '确定',
      cancelText: '取消',
      onOk() {
        router.push('/announcement/index');
      }
    });
  } else {
    router.push('/announcement/index');
  }
};

// 返回上一页
const handleBack = () => {
  if (window.history.length > 1) {
    router.back();
  } else {
    router.push('/announcement/index');
  }
};

// 页面加载时获取数据
onMounted(() => {
  getEditData();
});
</script>

<style lang="less" scoped>
.announcement-edit {
  padding: 20px;
  background-color: #ffffff;
  min-height: calc(100vh - 70px);
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;

  h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #2c3e50;
  }
}

.form-container {
  display: flex;
  justify-content: center;
  padding: 10px 0;
}

.edit-form {
  max-width: 900px; // 增加最大宽度
  width: 100%;
  font-size: 14px; // 调整基础字体大小
}

/* 增大输入框 */
.large-input {
  font-size: 14px;
  line-height: 1.5;
  .ant-input {
    font-size: 14px;
    height: 40px; // 增加输入框高度
  }
}

/* 增大文本域 */
.large-textarea {
  font-size: 14px;
  line-height: 1.6;
  .ant-input-textarea {
    font-size: 14px;
  }
}

/* 调整标签字体 */
.ant-form-item-label > label {
  font-size: 14px;
  font-weight: 500;
}

/* 调整单选框字体 */
.ant-radio-wrapper {
  font-size: 14px;
}

.upload-hint {
  margin-top: 8px;
  color: #8c8c8c;
  font-size: 13px;
}

.form-actions {
  display: flex;
  justify-content: center;
  padding: 16px 0;
  margin-top: 24px;
  
  .ant-btn {
    font-size: 14px;
    padding: 6px 16px;
  }
}
</style>